import React, { useState } from 'react';
import { Button } from '@shadcn/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@shadcn/components/ui/dropdown-menu';
import { Download, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import ticketApi from '../../services/ticketApi';

// Thêm import xlsx nếu có
let XLSX;
try {
  XLSX = require('xlsx');
} catch (e) {
  XLSX = null;
}

/**
 * Export formats configuration
 */
const EXPORT_FORMATS = {
  CSV: {
    label: 'CSV',
    icon: FileText,
    mimeType: 'text/csv',
    extension: 'csv'
  },
  EXCEL: {
    label: 'Excel (XLSX)',
    icon: FileSpreadsheet,
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'xlsx'
  }
};

/**
 * Utility function to convert ticket data to CSV (table fields only)
 */
const jsonToCsv = (data) => {
  if (!data || data.length === 0) return '';
  
  // Extract only table-visible fields from each ticket
  const tableData = data.map(extractTableFields);
  
  // Define column order to match table layout
  const columns = [
    { key: 'id', label: 'Ticket ID' },
    { key: 'type', label: 'Type' },
    { key: 'customer', label: 'Customer Name' },
    { key: 'staff', label: 'Staff Name' },
    { key: 'order_number', label: 'Order Number' },
    { key: 'store', label: 'Store Name' },
    { key: 'meta_status', label: 'Status' },
    { key: 'original_status', label: 'Original Status' },
    { key: 'created_date', label: 'Created Date' }
  ];
  
  // Create header row
  const header = columns.map(col => `"${col.label}"`).join(',');
  
  // Create data rows
  const rows = tableData.map(ticket => {
    return columns.map(col => {
      let value = ticket[col.key];
      
      // Handle different data types
      if (value === null || value === undefined) {
        value = '';
      } else if (typeof value === 'boolean') {
        value = value ? 'true' : 'false';
      } else if (typeof value === 'number') {
        value = value.toString();
      } else {
        value = String(value);
        
        // Format date if it's the created_date field
        if (col.key === 'created_date' && value) {
          try {
            const date = new Date(value);
            value = date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          } catch {
            // Keep original value if date parsing fails
          }
        }
      }
      
      // Always wrap in quotes and escape internal quotes for CSV safety
      return `"${value.replace(/"/g, '""')}"`;
    }).join(',');
  });
  
  return [header, ...rows].join('\n');
};

/**
 * Extract only table-visible fields from ticket data
 * This matches what users see in the table interface
 */
const extractTableFields = (ticket) => {
  return {
    id: ticket.id || '',
    type: ticket.ticketType || '',
    customer: ticket.customerName || '',
    staff: ticket.staffName || '',
    order_number: ticket.orderNumber || '',
    store: ticket.storeName || '',
    meta_status: ticket.status?.metaStatus || '',
    original_status: ticket.status?.originalStatus || '',
    created_date: ticket.createdAt || ''
  };
};

/**
 * Download file utility
 */
const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Generate filename with timestamp
 */
const generateFilename = (prefix, extension) => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
  return `${prefix}_${timestamp}.${extension}`;
};

/**
 * ExportButton Component
 * Reusable export functionality for ticket data
 * 
 * @param {Object} props
 * @param {Object} props.filters - Current filters to apply to export
 * @param {string} props.filename - Base filename (default: 'tickets')
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {'button'|'dropdown'} props.variant - Display variant (default: 'dropdown')
 * @param {'sm'|'md'|'lg'} props.size - Button size (default: 'md')
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onExportStart - Callback when export starts
 * @param {Function} props.onExportComplete - Callback when export completes
 * @param {Function} props.onExportError - Callback when export fails
 */
const ExportButton = ({ 
  filters = {}, 
  filename = 'tickets',
  disabled = false,
  variant = 'dropdown',
  size = 'md',
  className = '',
  onExportStart,
  onExportComplete,
  onExportError
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState(null);

  /**
   * Handle export for specific format
   */
  const handleExport = async (format) => {
    if (isExporting) return;
    
    setIsExporting(true);
    setExportingFormat(format);
    
    try {
      onExportStart?.(format);
      
      // Fetch data from API
      const data = await ticketApi.exportTickets(filters);
      
      if (!data || data.length === 0) {
        throw new Error('No data to export');
      }
      
      const formatConfig = EXPORT_FORMATS[format];
      const exportFilename = generateFilename(filename, formatConfig.extension);
      
      if (format === 'EXCEL' && XLSX) {
        // Chuyển data sang sheet
        const tableData = data.map(extractTableFields);
        const ws = XLSX.utils.json_to_sheet(tableData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Tickets');
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: formatConfig.mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = exportFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // fallback CSV
        const csvContent = jsonToCsv(data);
        downloadFile(csvContent, exportFilename, 'text/csv');
      }
      
      onExportComplete?.(format, data.length);
      
    } catch (error) {
      console.error('Export failed:', error);
      onExportError?.(error, format);
    } finally {
      setIsExporting(false);
      setExportingFormat(null);
    }
  };

  /**
   * Render single export button
   */
  const renderSingleButton = (format) => {
    const formatConfig = EXPORT_FORMATS[format];
    const IconComponent = formatConfig.icon;
    const isCurrentlyExporting = isExporting && exportingFormat === format;
    
    return (
      <Button
        onClick={() => handleExport(format)}
        disabled={disabled || isExporting}
        size={size}
        className={`flex items-center gap-2 ${className}`}
      >
        {isCurrentlyExporting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <IconComponent className="w-4 h-4" />
        )}
        {isCurrentlyExporting ? 'Exporting...' : `Export ${formatConfig.label}`}
      </Button>
    );
  };

  /**
   * Render dropdown with multiple formats
   */
  const renderDropdown = () => {
      return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          disabled={disabled || isExporting}
          size={size}
          className={`flex items-center justify-center gap-2 ${className}`}
        >
          {isExporting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          {isExporting ? 'Exporting...' : 'Export'}
        </Button>
      </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {Object.entries(EXPORT_FORMATS).map(([key, config]) => {
            const IconComponent = config.icon;
            const isCurrentlyExporting = isExporting && exportingFormat === key;
            
            return (
              <DropdownMenuItem
                key={key}
                onClick={() => handleExport(key)}
                disabled={isExporting}
                className="flex items-center gap-2 cursor-pointer"
              >
                {isCurrentlyExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <IconComponent className="w-4 h-4" />
                )}
                {config.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  // For single button variant, default to CSV
  if (variant === 'button') {
    return renderSingleButton('CSV');
  }

  return renderDropdown();
};

/**
 * Quick CSV Export Button
 */
export const QuickCSVExport = (props) => (
  <ExportButton {...props} variant="button" />
);

/**
 * Export with custom data (not from API)
 */
export const ExportCustomData = ({ 
  data, 
  filename = 'export', 
  format = 'CSV',
  children,
  className = ''
}) => {
  const [isExporting, setIsExporting] = useState(false);
  
  const handleExport = async () => {
    if (isExporting || !data || data.length === 0) return;
    
    setIsExporting(true);
    
    try {
      const formatConfig = EXPORT_FORMATS[format];
      const exportFilename = generateFilename(filename, formatConfig.extension);
      
      if (format === 'CSV') {
        const csvContent = jsonToCsv(data);
        downloadFile(csvContent, exportFilename, formatConfig.mimeType);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };
  
  return (
    <Button
      onClick={handleExport}
      disabled={isExporting || !data || data.length === 0}
      className={`flex items-center gap-2 ${className}`}
    >
      {isExporting ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Download className="w-4 h-4" />
      )}
      {children || (isExporting ? 'Exporting...' : 'Export')}
    </Button>
  );
};

export { ExportButton as default };
export { ExportButton };
